// DEPENDENCIES
import moment from "moment";

// TYPES
import { Field, OnChange } from "../types";

// UTILS
import { capitalizeString, translateCorporate } from "../app/utils";
import {
  asCuit,
  civilStatusOptions,
  genderOptions,
  isAllariaMasOnboarding,
  isMobileViewport,
  isUnifiedOnboarding,
  maxFileSize,
  pepOptions,
  validBusinessDocumentationTypes,
} from "../utils";
import {
  birthDateRequired,
  cbuRequired,
  confirmEmailRequired,
  cuitPFRequired,
  cuitPJRequired,
  cuitSpouse,
  dniRequired,
  emailRequired,
  filesRequired,
  largeStringRequired,
  mediumStringRequired,
  noSpecialCharactersStringRequired,
  numberRequired,
  pastDateStringValidation,
  percentageRequired,
  phone,
  radioOrSingleSelectRequired,
  smallString,
  smallStringLettersRequired,
  smallStringRequired,
  stringNotRequired,
  stringRequired,
  tramitNumberRequired,
  zipCodeRequired,
} from "../validations";
import UnificationCard from "../components/UnificationCard/UnificationCard";
import { Alert } from "@allaria-tech/matter/Alert";
import { getCompanyData } from "../utils/companies";

const showUnifiedAlert = isUnifiedOnboarding() && isAllariaMasOnboarding();
const showEmailWarningAlert = [
  "ALLARIA",
  "ALLARIAMAS",
  "AGROFINANZAS",
].includes(getCompanyData().companyName);

const SELECT_PLACEHOLDER = "Seleccioná una opción";
const DETAIL_PLACEHOLDER = "Escribí el detalle";
const CUIT_PLACEHOLDER = "XX-XXXXXXXX-X";

const corporateTypeOptions = Object.values(translateCorporate).map((value) => ({
  value,
  label: value,
}));

const shareholderTaxIdOptions = ["CUIT", "NIF"].map((taxId) => ({
  value: taxId,
  label: taxId,
}));

const registrationAuthorityOptions = [
  {
    label: "Inspección General de Justicia",
    value: "GENERAL_INSPECTION_OF_JUSTICE",
  },
  {
    label: "Dirección de Personas Jurídicas",
    value: "DIRECTORATE_OF_LEGAL_ENTITIES",
  },
  { label: "Otro", value: "OTHER" },
];

export const yesOrNoBoolOptions = [
  { value: "SI", label: "Sí" },
  { value: "NO", label: "No" },
];

const yesOrNo = radioOrSingleSelectRequired(yesOrNoBoolOptions);

export const personInputs = {
  cuitPF: {
    name: "cuit",
    type: "cuit",
    label: "CUIT/CUIL",
    placeholder: CUIT_PLACEHOLDER,
    initialValue: "",
    preconfirmationLabel: "CUIT/CUIL",
    validation: cuitPFRequired,
    formatInPreConfirmation: (value) => (value ? asCuit(value) : "-"),
  },
  email: {
    name: "email",
    type: "email",
    label: "Mail",
    initialValue: "",
    validation: emailRequired,
    placeholder: "direccion@mail.com",
    disabledByUrlParams: ["email"],
  },
  confirmEmail: {
    name: "confirmEmail",
    type: "email",
    label: "Confirmar mail",
    placeholder: "direccion@mail.com",
    initialValue: "",
    validation: confirmEmailRequired,
  },
  identityDocument: {
    name: "identityDocument",
    type: "dni",
    label: "DNI",
    placeholder: "XX.XXX.XXX",
    initialValue: "",
    validation: dniRequired,
    columns: 2,
  },
  tramitNumber: {
    name: "tramitNumber",
    type: "text",
    label: "Número de trámite",
    placeholder: "XXXXXXXXXXX",
    initialValue: "",
    validation: tramitNumberRequired,
    tooltip: {
      label: "¿Dónde lo encuentro?",
      modal: "modal-tramit-number",
    },
    infoText:
      "Debe contener 11 dígitos. Si es necesario, agregá ceros al comienzo",
    inputMode: "numeric",
    maxLength: 11,
    columns: 2,
  },
  gender: {
    name: "gender",
    type: "select",
    label: "Género",
    placeholder: SELECT_PLACEHOLDER,
    initialValue: "",
    validation: radioOrSingleSelectRequired(genderOptions),
    options: genderOptions,
  },
  telephone: {
    name: "telephone",
    type: "phone",
    label: "Teléfono celular",
    initialValue: "",
    validation: phone,
    inputMode: "numeric",
    infoText: "Con código de área sin 0 ni 15.",
    formatInPreConfirmation: (value) =>
      value && value.startsWith("+") ? value : `+${value}`,
  },
  referredBool: {
    name: "referredBool",
    type: "radio",
    label: "¿Fuiste referido?",
    initialValue: "",
    validation: radioOrSingleSelectRequired(yesOrNoBoolOptions),
    options: yesOrNoBoolOptions,
    disabledByUrlParams: "ref",
  },
  referrer: {
    name: "referrer",
    type: "text",
    label: "Nombre o mail de quien te refirió",
    initialValue: "",
    conditional: {
      name: "referredBool",
      value: "SI",
    },
    validation: smallStringRequired,
    disabledByUrlParams: "ref",
  },
  name: {
    name: "name",
    type: "text",
    label: "Nombre/s",
    validation: smallStringLettersRequired,
    initialValue: "",
    columns: 2,
  },
  lastName: {
    name: "lastName",
    type: "text",
    label: "Apellido/s",
    validation: smallStringLettersRequired,
    initialValue: "",
    columns: 2,
    formatInPreConfirmation: (value) => capitalizeString(value),
  },
  citizenship: {
    name: "citizenship",
    type: "select",
    searchable: true,
    label: "Nacionalidad",
    placeholder: SELECT_PLACEHOLDER,
    selectType: "single",
    initialValue: "",
    columns: 2,
    formatInPreConfirmation: (value) => (value ? capitalizeString(value) : "-"),
    disabled: (state) => !!state?.onboardingData?.formValues?.citizenship,
    onChange: (_values, setFieldValue, _, _setFieldTouched, value) => {
      setFieldValue("citizenship", value);
    },
  },
  birthDate: {
    name: "birthDate",
    type: "date-picker",
    label: "Fecha de nacimiento",
    minDatePickerDate: moment("1900-01-01").toDate(),
    maxDatePickerDate: moment().subtract(1, "days").toDate(),
    validation: birthDateRequired,
    initialValue: "",
    columns: 2,
    formatInPreConfirmation: (value) =>
      value
        ? moment(value, ["DD/MM/YYYY", "YYYY-MM-DD"], true).format("DD/MM/YYYY")
        : "-",
    disabled: (state) => !!state?.onboardingData?.formValues?.birthDate,
  },
  civilStatus: {
    name: "civilStatus",
    type: "select",
    label: "Estado civil",
    placeholder: SELECT_PLACEHOLDER,
    selectType: "single",
    initialValue: "",
    validation: radioOrSingleSelectRequired(civilStatusOptions),
    options: civilStatusOptions,
    columns: 2,
  },
  spouseName: {
    name: "spouseName",
    type: "text",
    label: "Nombre/s del cónyuge",
    validation: smallStringLettersRequired,
    initialValue: "",
    columns: 2,
    conditional: { name: "civilStatus", value: "MARRIED" },
  },
  spouseLastName: {
    name: "spouseLastName",
    type: "text",
    label: "Apellido/s del cónyuge",
    validation: smallStringLettersRequired,
    initialValue: "",
    columns: 2,
    conditional: { name: "civilStatus", value: "MARRIED" },
  },
  spouseCuil: {
    name: "spouseCuil",
    type: "cuit",
    label: "CUIL del cónyuge",
    placeholder: CUIT_PLACEHOLDER,
    initialValue: "",
    showOptionalLabel: true,
    formatInPreConfirmation: (value) => (value ? asCuit(value) : "-"),
    conditional: { name: "civilStatus", value: "MARRIED" },
    validation: cuitSpouse,
    columns: 2,
  },
  pep: {
    name: "pep",
    type: "select",
    label: "¿Estás expuesto/a políticamente?",
    placeholder: SELECT_PLACEHOLDER,
    selectType: "single",
    initialValue: "",
    preconfirmationLabel: "¿Expuesto/a políticamente?",
    tooltip: {
      modal: "modal-PEP",
      label: "¿Qué significa?",
    },
    validation: radioOrSingleSelectRequired(pepOptions),
    options: pepOptions,
    columns: 2,
    startsRow: true,
  },
  pepDetails: {
    name: "pepDetails",
    type: "text",
    label: "Describí brevemente la situación",
    placeholder: DETAIL_PLACEHOLDER,
    validation: largeStringRequired,
    initialValue: "",
    conditional: { name: "pep", value: ["DIRECT", "FAMILY"] },
    columns: 2,
  },
} satisfies Record<string, Field>;

export const entityInputs = {
  emailWarningAlert: {
    name: "emailWarningAlert",
    type: "alert",
    label: "",
    initialValue: "",
    hideField: !showEmailWarningAlert,
    alertContent: () => (
      <Alert
        variant="INFO"
        className="mb-4"
        title="Importante"
        description="Este mail se vinculará permanentemente al primer representante que registres para tu empresa. Aconsejamos que sea un correo personal y al que esa persona tenga acceso. Este mail no podrá ser desvinculado posteriormente."
      />
    ),
  },
  cuitPJ: {
    name: "businessInformation.cuit",
    type: "cuit",
    label: "CUIT de la empresa",
    placeholder: CUIT_PLACEHOLDER,
    initialValue: "",
    preconfirmationLabel: "CUIT/CUIL",
    validation: cuitPJRequired,
    formatInPreConfirmation: (value) => (value ? asCuit(value) : "-"),
    disabledByUrlParams: ["cuit"],
  },
  legalName: {
    name: "legalName",
    type: "text",
    label: "Razón social",
    validation: smallStringRequired,
    initialValue: "",
  },
  corporateType: {
    name: "businessInformation.corporateType",
    type: "select",
    label: "Tipo de sociedad",
    placeholder: "Seleccioná la sociedad que corresponda",
    selectType: "single",
    initialValue: "",
    validation: radioOrSingleSelectRequired(corporateTypeOptions),
    options: corporateTypeOptions,
  },
  registrationCountry: {
    name: "businessInformation.registrationCountry",
    type: "select",
    label: "País de registro",
    searchable: true,
    placeholder: SELECT_PLACEHOLDER,
    selectType: "single",
    initialValue: "",
    columns: 2,
  },
  registrationDate: {
    name: "businessInformation.registrationDate",
    type: "date-picker",
    label: "Fecha de registro",
    initialValue: "",
    minDatePickerDate: moment().subtract(100, "years").toDate(),
    maxDatePickerDate: moment().subtract(1, "day").toDate(),
    validation: pastDateStringValidation,
    formatInPreConfirmation: (value) =>
      value
        ? moment(value, ["DD/MM/YYYY", "YYYY-MM-DD"], true).format("DD/MM/YYYY")
        : "-",
    columns: 2,
  },
  registrationProvince: {
    name: "businessInformation.registrationProvince",
    type: "select",
    label: "Provincia de constitución",
    initialValue: "",
    searchable: true,
    selectType: "single",
    dontScrollTo: true,
  },
  registrationNumber: {
    name: "businessInformation.registrationNumber",
    type: "text",
    label: "Número de inscripción",
    initialValue: "",
    inputMode: "numeric",
    validation: numberRequired,
    tooltip: {
      label: "¿Dónde lo encuentro?",
      content:
        "El número de inscripción de la empresa figura en la oblea de inscripción del estatuto",
    },
    columns: 2,
  },
  registrationAuthority: {
    name: "businessInformation.registrationAuthority",
    type: "select",
    label: "Organismo de inscripción",
    placeholder: SELECT_PLACEHOLDER,
    selectType: "single",
    initialValue: "",
    validation: radioOrSingleSelectRequired(registrationAuthorityOptions),
    options: registrationAuthorityOptions,
    tooltip: {
      content: "El organismo bajo el cual registraste la sociedad.",
    },
    columns: 2,
  },
  registrationAuthorityDetails: {
    name: "businessInformation.registrationAuthorityDetails",
    type: "text",
    label: "Detalle de organismo de inscripción",
    placeholder: DETAIL_PLACEHOLDER,
    initialValue: "",
    validation: stringRequired,
    conditional: {
      name: "businessInformation.registrationAuthority",
      value: "OTHER",
    },
  },
  hasJointSignature: {
    name: "businessInformation.hasJointSignature",
    type: "radio",
    label: "¿Tiene firma conjunta?",
    initialValue: "",
    preconfirmationLabel: "Firma conjunta",
    validation: yesOrNo,
    options: yesOrNoBoolOptions,
    tooltip: {
      content:
        "La firma conjunta exige el consentimiento de distintos representantes. Dicho requisito puede ser establecido en el instrumento constitutivo o en poderes emitidos por la entidad",
    },
    columns: 2,
  },
  isPublicEntity: {
    name: "businessInformation.isPublicEntity",
    type: "radio",
    label: "¿Es una entidad pública?",
    initialValue: "",
    preconfirmationLabel: "Entidad pública",
    validation: yesOrNo,
    options: yesOrNoBoolOptions,
    tooltip: {
      content: "Posee una participación estatal del 20% o más.",
    },
    columns: 2,
  },
  isInEconomicGroup: {
    name: "businessInformation.isInEconomicGroup",
    type: "radio",
    label: "¿Es parte de un grupo económico?",
    initialValue: "",
    preconfirmationLabel: "Grupo económico",
    validation: yesOrNo,
    options: yesOrNoBoolOptions,
    tooltip: {
      content:
        "Un grupo económico se conforma por dos o más empresas vinculadas por una relación de control o pertenencia a una misma organización.",
    },
    columns: 2,
  },
  isComplexStructure: {
    name: "businessInformation.isComplexStructure",
    type: "radio",
    label: "¿Tiene una estructura societaria compleja?",
    initialValue: "",
    validation: yesOrNo,
    options: yesOrNoBoolOptions,
    tooltip: {
      content:
        "Una estructura compleja se da cuando la composición accionaria de la sociedad está muy atomizada y/o estructurada",
    },
    columns: 2,
  },
  bankAccountBool: {
    name: "businessInformation.bankAccountBool",
    type: "radio",
    label: "¿Tenés cuenta bancaria en Argentina?",
    columns: 2,
    initialValue: "",
    validation: yesOrNo,
    options: yesOrNoBoolOptions,
  },
  bankAccounts: {
    name: "businessInformation.bankAccounts",
    type: "text",
    label: "Cuenta bancaria en Argentina",
    placeholder: "Ingresá tu CBU o CVU",
    initialValue: "",
    preconfirmationLabel: "Cuenta bancaria argentina",
    validation: cbuRequired,
    conditional: { name: "businessInformation.bankAccountBool", value: "SI" },
    columns: 2,
    description:
      "El presente CBU informado será utilizado como instrucción permanente para pagos futuros a menos que se indique lo contrario. Luego de la apertura de cuenta, se podrán editar y agregar nuevos CBU/CVUs.",
  },
  businessForeignTaxDetailBool: {
    name: "businessInformation.foreignTaxDetailBool",
    type: "radio",
    label: "¿Tributa en país extranjero?",
    initialValue: "",
    validation: yesOrNo,
    options: yesOrNoBoolOptions,
    startsRow: true,
  },
  businessForeignTaxId: {
    name: "businessInformation.foreignTaxDetail.idNumber",
    type: "text",
    label: "Identificador fiscal extranjero",
    placeholder: "Ingresá NIF, RUC, CDI u otro identificador fiscal",
    initialValue: "",
    validation: noSpecialCharactersStringRequired,
    conditional: {
      name: "businessInformation.foreignTaxDetailBool",
      value: "SI",
    },
  },
  document: {
    name: "document",
    type: "file",
    label: "Documento",
    validation: filesRequired(validBusinessDocumentationTypes, maxFileSize),
    initialValue: "",
    canUploadMultipleFiles: true,
    maxSize: maxFileSize,
    validTypes: validBusinessDocumentationTypes,
  },
} satisfies Record<string, Field>;

export const sharedInputs = {
  activities: {
    name: "activities",
    type: "combobox",
    label: "Rubro o actividad",
    placeholder: "Seleccioná las actividades que correspondan",
    selectType: "multiple-checkbox",
    confirmSelection: isMobileViewport(),
    initialValue: "",
  },
  activitiesDetails: {
    name: "activitiesDetails",
    type: "text",
    label: "Detalle de la actividad",
    placeholder: DETAIL_PLACEHOLDER,
    initialValue: "",
    validation: stringNotRequired,
    tooltip: {
      content:
        "En este campo podes detallar aun más la actividad seleccionada, de modo que nos permita realizar un análisis completo y preciso",
    },
  },
  "address.country": {
    name: "address.country",
    type: "select",
    label: "País de residencia",
    searchable: true,
    placeholder: SELECT_PLACEHOLDER,
    selectType: "single",
    initialValue: "",
  },
  "address.fullAddress": {
    name: "address.fullAddress",
    type: "address",
    label: "Domicilio",
    validation: mediumStringRequired,
    initialValue: "",
    autoComplete: "off",
    // Only the street
    formatInPreConfirmation: (value) => value?.split(",")?.[0],
  },
  "address.floor": {
    name: "address.floor",
    type: "text",
    label: "Piso",
    validation: smallString,
    initialValue: "",
    columns: 2,
  },
  "address.apartment": {
    name: "address.apartment",
    type: "text",
    label: "Departamento",
    validation: smallString,
    initialValue: "",
    columns: 2,
  },
  "address.zipCode": {
    name: "address.zipCode",
    type: "text",
    label: "Código postal",
    validation: zipCodeRequired,
    initialValue: "",
    columns: 1,
  },
  "address.municipality": {
    name: "address.municipality",
    type: "text",
    label: "Localidad",
    validation: smallStringRequired,
    initialValue: "",
    columns: 2,
  },
  "address.state": {
    name: "address.state",
    type: "select",
    label: "Provincia",
    initialValue: "",
    searchable: true,
    columns: 2,
  },
  ownership: {
    name: "ownership",
    type: "number",
    label: "Porcentaje de participación",
    validation: percentageRequired,
    isNumberPercentage: true,
    inputMode: "numeric",
    initialValue: "",
    formatInPreConfirmation: (value) => `${value}%`,
  },
  taxType: {
    name: "taxType",
    type: "select",
    label: "Tipo de identificación fiscal",
    placeholder: SELECT_PLACEHOLDER,
    selectType: "single",
    initialValue: "",
    tooltip: {
      content:
        "El CUIT (Clave Única de Identificación Tributaria) es el número que identifica a personas en Argentina. El NIF (Número de Identificación Fiscal) es el código que identifica a personas en otros países. Podés ingresar tu RUC, CDI u otro identificador fiscal equivalente.",
    },
    validation: radioOrSingleSelectRequired(shareholderTaxIdOptions),
    options: shareholderTaxIdOptions,
    columns: 2,
  },
  taxId: {
    name: "taxId",
    type: "cuit",
    dynamicType: (formik) => {
      if (!formik) throw new Error("Error: state not found");

      const { taxType } = formik.values;

      if (taxType === "CUIT") return "cuit";
      else return "text";
    },
    label: "Identificación fiscal",
    initialValue: "",
    columns: 2,
    dontScrollTo: true,
    formatInPreConfirmation: (value) => (value ? asCuit(value) : "-"),
  },
  foreignTaxDetailBool: {
    name: "foreignTaxDetailBool",
    type: "radio",
    label: "¿Tiene residencia fiscal fuera de Argentina?",
    initialValue: "",
    validation: yesOrNo,
    options: yesOrNoBoolOptions,
  },
  "foreignTaxDetail.idNumber": {
    name: "foreignTaxDetail.idNumber",
    type: "text",
    label: "Identificación fiscal",
    validation: noSpecialCharactersStringRequired,
    initialValue: "",
    conditional: { name: "foreignTaxDetailBool", value: "SI" },
  },
  unifiedLabel: {
    name: "unifiedLabel",
    type: "label",
    label: "",
    initialValue: "",
    hideField: !showUnifiedAlert,
    text: <UnificationCard />,
  },
} satisfies Record<string, Field>;

export const inputs = {
  ...personInputs,
  ...entityInputs,
  ...sharedInputs,
};

export type InputName = keyof typeof inputs;

export const input = (
  name: InputName,
  overrides: Partial<Field> = {},
): Field => ({
  ...inputs[name],
  ...overrides,
});

type FieldRef = InputName | [InputName, Partial<Field>] | Field;

export const fieldsFrom = (...refs: FieldRef[]): Field[] =>
  refs.map((ref) => {
    if (typeof ref === "string") return input(ref);
    if (Array.isArray(ref)) return input(ref[0], ref[1]);
    return ref;
  });

// Some inputs are never asked for on their own — a step that collects any one of
// them collects the whole set, in the same order. `fieldGroup` names such a set
// once; the returned helper takes `overrides` keyed by input name, so a caller
// can swap a single member's validation or conditional without restating the
// group.
const fieldGroup =
  <T extends InputName>(...names: T[]) =>
  (overrides: Partial<Record<T, Partial<Field>>> = {}): Field[] =>
    names.map((name) => input(name, overrides[name]));

export const spouseFields = fieldGroup(
  "spouseName",
  "spouseLastName",
  "spouseCuil",
);

export const pepFields = fieldGroup("pep", "pepDetails");

const addressInputNames = [
  "address.country",
  "address.fullAddress",
  "address.floor",
  "address.apartment",
  "address.zipCode",
  "address.municipality",
  "address.state",
] as const satisfies readonly InputName[];

type AddressInputName = (typeof addressInputNames)[number];

export const addressFields = (
  prefix: string,
  overrides: Partial<Record<AddressInputName, Partial<Field>>> = {},
): Field[] =>
  addressInputNames.map((name) =>
    input(name, {
      name: `${prefix}.${name.slice("address.".length)}`,
      ...overrides[name],
    }),
  );

export const clearAddressOf =
  (prefix: string): OnChange =>
  (_, setFieldValue) =>
    [
      "fullAddress",
      "municipality",
      "state",
      "zipCode",
      "city",
      "street",
      "apartment",
      "floor",
      "apartment",
    ].forEach((field) => setFieldValue(`${prefix}.${field}`, ""));
